<template>
  <form
    :id="formId"
    class="prod-form"
    :class="[
      { 'is-loading': parentState === 'loading' },
      { 'is-initial': parentState === 'initialLoad' },
      { 'has-image': showProductMedia },
      { 'has-badges': filteredBadges?.length },
    ]"
  >
    <component
      :is="mediaElement"
      v-if="showProductMedia"
      class="prod-form__image"
      :href="mediaUrl"
    >
      <responsive-image
        :aspect-ratio="`0.728`"
        :image="productMedia"
        :sizes="mediaSize"
      />
    </component>

    <header class="prod-form__header">
      <template
        v-if="titleIsLink"
      >
        <a
          :href="mediaUrl"
        >
          <component
            :is="titleElement"
            :id="sectionTitle"
            class="prod-form__product-title"
            :class="{ 'prod-form__no-swatch-sizeguide': !(displaySwatches || displaySizeGuide) }"
            v-text="productTitle"
          />
        </a>
      </template>
      <template v-else>
        <component
          :is="titleElement"
          :id="sectionTitle"
          class="prod-form__product-title"
          :class="{ 'prod-form__no-swatch-sizeguide': !(displaySwatches || displaySizeGuide) }"
          v-text="productTitle"
        />
      </template>

      <product-prices
        class="prod-form__product-price"
        :product="product"
      />
    </header>

    <div
      v-if="styleLabel || displaySwatches || displaySizeGuide"
      class="prod-form__swatches"
    >
      <span
        v-if="styleLabel"
        class="prod-form__swatches-title"
        v-text="styleLabel"
      />

      <product-swatches
        v-if="displaySwatches"
        class="prod-form__swatches-list"
        :class="{ 'is-loading': parentState !== 'ready' }"
        element-type="button"
        :linked-styles="product.linkedStyles"
        :product-handle="product.handle"
        @swatch-image-clicked="handleSwatchUpdate"
        @swatch-image-hover="handleSwatchHover"
      />

      <btn
        v-if="displaySizeGuide"
        class="prod-form__size-guide"
        :label="sizeGuideLabel"
        icon="size-guide"
        type="button"
        @click.prevent="toggleSizeGuidePopup"
      />
    </div>

    <product-variant-selector
      v-if="product.handle"
      class="prod-form__variant-selector"
      :current-variant="currentVariant"
      :display-add-to-cart="true"
      :display-wishlist="true"
      :length-heading-text="variantSelectorLengthHeading"
      :parent-state="state"
      :product="product"
      :type="type"
      :filtered-badges="filteredBadges"
      @variant-updated="handleVariantUpdate"
      @add-product-to-cart="handleAddToCart"
    />
  </form>
</template>
<script>

/**
 * Component / Product Page: Product Form
 * ------------------------------------------------------------------------------
 * - Product Form for the main PDP template.
 * @param {Boolean} enableSizeGuide - Display the Size Guide button.
 * @param {Boolean} isMobile - If the current view is mobile, from Pinia.
 * @param {String} liquid.product - Formatted Liquid product object.
 * @param {String} liquid.sectionId - Liquid section ID
 * @param {Object} [parentCurrentVariant] - currentVariant object from parent component, used for main product form only.
 * @param {String} parentState - Component state of the parent component.
 * @param {Object} product - Formatted product object.
 * @param {Boolean} [showProductMedia] - If to show the pack-shot or not. Does not display if there is no CTL for the PDP.
 * @param {Boolean} [showSwatches] - If to display product swatches. Disabled on STL product forms.
 * @param {String} liquid.sectionId - Sections Liquid ID.
 */
import { mapActions } from 'pinia'
import { validate as uuidValidate, version as uuidVersion } from 'uuid'

import breakpoints from '~/helpers/breakpoints'
import { getItemProperties } from '~/helpers/product'
import { getPackShotImage } from '~/helpers/utils'
import setDataLayerValues from '~/helpers/elevar-data-layer'

import { useCartStore } from '~stores/cart'
import { useWishlistStore } from '~stores/wishlist'

import Btn from '~components/btn/btn'
import ProductPrices from '~components/product-prices/product-prices'
import ProductSwatches from '~components/product-swatches/product-swatches'
import ProductVariantSelector from '~components/product-variant-selector/product-variant-selector'
import ResponsiveImage from '~components/responsive-image/responsive-image'

export default {
  name: 'ProductForm',

  components: {
    Btn,
    ProductPrices,
    ProductSwatches,
    ProductVariantSelector,
    ResponsiveImage,
  },

  props: {
    enableSizeGuide: {
      type: Boolean,
      default: false,
    },
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    isMobile: {
      type: Boolean,
      default: true,
    },
    parentCurrentVariant: {
      type: [Boolean, Object],
      default: false,
    },
    parentState: {
      type: String,
      default: 'loading',
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
    showProductMedia: {
      type: Boolean,
      default: true,
    },
    showSwatches: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'default',
    },
  },

  emits: ['update-current-product'],

  data() {
    return {
      currentColour: false,
      currentVariant: false,
      marketingDataSource: false,
      state: 'loading',
    }
  },

  computed: {

    /**
     * Calculate if to display the swatches.
     * Show if:
     * - Enabled via Liquid
     * - Not a STL form
     * - There are actually linked products.
     * @returns {Boolean}
     */
    displaySwatches() {
      if (
        !this.showSwatches ||
        this.type === 'shop-the-look' ||
        !this.product.handle
      ) {
        return false
      }

      return Boolean(
        this.showSwatches &&
          (this.product.linkedStyles && this.product.linkedStyles.length),
      )
    },

    /**
     * Calculate the badges to display.
     *
     * - Display only for product-forms (`main-product` and `stl-product`).
     * - Currently exists only for `coming soon` badge.
     *
     * @returns {array|boolean}
     */
    filteredBadges() {
      if (!this.product?.badges?.length) {
        return false
      }

      const filteredBadges = this.product.badges.map((badge) => {
        const badgeName = badge.name.toLowerCase().trim()

        if (badgeName.includes('coming soon')) {
          return badge
        }

        return false
      }).filter(Boolean)

      return filteredBadges
    },

    /**
     * Calculate custom text for the Variant Selector 'Length' heading.
     * @returns {String}
     */
    variantSelectorLengthHeading() {
      return mint.strings.productDetailPage.productForm.selectLengthHeading
    },

    /**
     * Calculate if to display the Size Guide button.
     * @returns {Boolean}
     */
    displaySizeGuide() {
      if (
        !this.enableSizeGuide ||
        this.type === 'shop-the-look' ||
        !this.product.handle
      ) {
        return false
      }

      return true
    },

    /**
     * Calculate the 'Size Guide' button text.
     * @returns {String}
     */
    sizeGuideLabel() {
      return mint.strings.productDetailPage.productForm.sizeGuideButton
    },

    /**
     * Calculate the product title.
     * @returns {String}
     */
    productTitle() {
      return this.product.title
    },

    /**
     * Calculate if to wrap the title in an anchor tag
     * @returns {Boolean}
     */
    titleIsLink() {
      return this.type === 'shop-the-look'
    },

    /**
     * Return a unique ID for the form.
     * @returns {Void|String}
     */
    formId() {
      if (!this.product) {
        return null
      }

      return `product-form-${this.product.handle}-${this.product.key}`
    },

    /**
     * Calculate responsive srcset `sizes` attribute for the image.
     * @returns {String}
     */
    mediaSize() {
      return `
        (min-width: ${breakpoints.large}) 96px,
        86px
      `
    },

    /**
     * Calculate the element type for 'Shop The Look' image.
     * @returns {String|Null}
     */
    mediaElement() {
      return this.type === 'shop-the-look' ? 'a' : 'span'
    },

    /**
     * Calculate a hyperlink for 'Shop The Look' image.
     * - If the current product has 'FlexiFashion' enabled, append the handle to the URL.
     * - FlexiFashion will only come available if both:
     *   1) Flexi parameter is appears in the URL
     *   2) The product has some FlexiFashion metafields with the same name.
     * @returns {String|Null}
     */
    mediaUrl() {
      if (this.type !== 'shop-the-look') {
        return null
      }

      const mediaUrl = new URL(`${location.protocol}${this.product.url}`)
      const flexiFashionHandle = this.product?.flexiFashion?.handle || false

      if (flexiFashionHandle) {
        mediaUrl.searchParams.append('flexi', flexiFashionHandle)
      }

      return mediaUrl.toString()
    },

    /**
     * Calculate the primary pack shot image to display.
     * - Typically, the last two images will be pack shots, as ordered by Creative department.
     * - Will return the image that has '_7' in its 'src' within the regular expression patterns
     *   provided for Cloudinary and Shopify URLs respectively.
     * - Fallback: If more than 2 images, return the second-to-last, else get first.
     * @returns {Boolean|Object}
     */
    productMedia() {
      if (!this.product?.images || !this.product?.images.length) {
        return false
      }

      const packShot = getPackShotImage(this.product.images)

      return packShot.image || false
    },

    /**
     * Calculate the displayed text for the current colour.
     * @returns {String}
     */
    styleLabel() {
      if (!this.product?.colour?.name) {
        return false
      }

      return mint.strings.productDetailPage.productForm.colourLabel.replace(
        /{{ colour }}/g,
        this.currentColour,
      )
    },

    /**
     * Return a unique ID for the title element.
     * @returns {String|Void}
     */
    sectionTitle() {
      return this.titleElement === 'h1'
        ? `section-tite-${this.liquid.sectionId}`
        : null
    },

    /**
     * Calculate the type of element of the product title.
     * - Heading for main title, span for STL.
     * @returns {String}
     */
    titleElement() {
      return this.type === 'default' ? 'h1' : 'span'
    },
  },

  watch: {

    /**
     * Watch the current variant id.
     * - Used only on mount, oldValue will be empty.
     * - Used to pre-select variants when parameter is in the URL.
     */
    'parentCurrentVariant.id': {
      handler(newValue, oldValue) {
        if (oldValue) {
          return
        }

        this.currentVariant = this.parentCurrentVariant
      },
    },

    /**
     * The product handle has changed.
     * - Usually occurs on swatch update on the PDP form.
     */
    'product.key': {
      handler(newValue, oldValue) {

        /**
         * If the older product key is not a valid v4 UUID, return early.
         * - This prevents the current variant from being reset when product
         *   data is first loaded from the API, as the product key from Liquid
         *   is not generated using UUID.
         * - This validation is a little overkill as the differently-generated
         *   product keys could be distinguished easily with regex. That is to
         *   say, this check could easily be performed without the UUID package.
         */
        if (!uuidValidate(oldValue) || uuidVersion(oldValue) !== 4) {
          return
        }

        this.currentVariant = false
      },
    },
  },

  mounted() {
    if (this.parentCurrentVariant) {
      this.currentVariant = this.parentCurrentVariant
    }

    this.currentColour = this.product?.colour?.name || false

    this.setMarketingDataSource()

    this.state = 'ready'
  },

  methods: {

    /**
     * Map Cart Actions.
     */
    ...mapActions(useCartStore, ['addItems']),

    /**
     * Map Wishlist methods.
     */
    ...mapActions(useWishlistStore, ['openBackInStock']),

    /**
     * Set the marketing data source for Bloomreach.
     */
    setMarketingDataSource() {
      const dataSource = this.type === 'shop-the-look'
        ? 'pdpShopTheLook'
        : 'pdpMainProduct'

      this.marketingDataSource = mint.services.elevarSources[dataSource]
    },

    /**
     * The customer has interacted with a swatch.
     * @param {Object} payload - Swatch style 'Colour' object.
     */
    handleSwatchUpdate(payload) {
      if (payload.handle === this.product?.handle) {
        return
      }

      this.$emit('update-current-product', {
        linkedStyle: payload.handle,
      })
    },

    /**
     * The customer has interacted with a swatch.
     * @param {Boolean|String} [payload] - Swatch style 'Colour' name string.
     */
    handleSwatchHover(payload) {
      if (this.isMobile) {
        return
      }

      if (payload) {
        this.currentColour = payload
        return
      }

      this.currentColour = this.product?.colour?.name || false
    },

    /**
     * Events for when the variant has changed.
     * @param {Object} data - Payload object.
     * @param {Object} data.currentVariant - Formatted variant object.
     * @param {String} data.lastUpdatedOption - The most recent option that was changed.
     * @param {Boolean} [data.lastUpdatedOption] - If the customer has manually selected this.
     */
    handleVariantUpdate({
      currentVariant,
      lastUpdatedOption,
      isManualSelection,
    }) {

      /**
       * Variant is sold out and 'Back in stock' notifications are enabled.
       * - Only open when the user has manually made a selection, to prevent
       *   BIS automatically opening.
       * - Ensure that the `currentVariant` exists, as the `false` value also
       *   passes the condition and triggers `BackInStock` popup accordingly
       */
      if (
        isManualSelection &&
        this.product?.wishlist?.bisEnabled &&
        (currentVariant && !currentVariant.available)
      ) {
        this.handleBackInStock(currentVariant)
      }

      this.currentVariant = currentVariant

      if (this.type === 'shop-the-look') {
        return
      }

      this.$emit('update-current-product', {
        variant: currentVariant,
      })
    },

    /**
     * Handle the adding of a product to the cart.
     */
    async handleAddToCart() {

      /**
       * - parentCurrentVariant will be passed down from Pinia
       * - currentVariant is scoped to this component, usually for 'Complete The Look'
       * - Pinia/PDP will update parentCurrentVariant, whereas we don't need
       *   to notify other components of CTL variants.
       * - TL;DR: Main PDP form? parentCurrentVariant. CTL? currentVariant
       */
      const currentVariant = this.parentCurrentVariant
        ? this.parentCurrentVariant
        : this.currentVariant

      if (!currentVariant) {
        return
      }

      this.state = 'addingToBag'

      let settledFormState = 'ready'

      const items = {
        id: currentVariant.id,
        quantity: 1,
      }

      items.properties = getItemProperties(this.product, this.currentVariant)

      try {
        await this.addItems({
          data: items,
          meta: {
            ...this.product.attributes,
            source: {
              section: this.marketingDataSource,
            },
          },
        })

        this.state = 'addedToBag'

        /**
         * Add Elevar 'Add To Cart' GTM data layer information.
         */
        setDataLayerValues({
          flexiFashion: this.product?.attributes?.flexiFashionName || false,
          type: this.type,
        })

        /**
         * Reset the current variant after a successful Add to Bag.
         * - Changing this here will trigger an update in Pinia and reset
         * the variant selector.
         */
        this.currentVariant = false

      } catch (error) {
        settledFormState = 'error'

        mint.Log('fatal', 'Failed to add product to the cart', error, {
          product: this.product,
          variant: currentVariant,
        })

      } finally {
        setTimeout(() => {
          this.state = settledFormState
        }, 1000)
      }
    },

    /**
     * Trigger the Swym Wishlist 'Back In Stock' modal.
     * - helpers/wishlist.js was built with Liquid product objects in mind
     * - Therefore, rebuild a pseudo product object to pass over
     * - Wishlist will be replaced with a Pinia Store eventually.
     * @param {Object} currentVariant - Variant to trigger for.
     */
    async handleBackInStock(currentVariant) {
      try {
        await this.openBackInStock(this.product, currentVariant)

      } catch (error) {
        mint.Log('error', 'Could not open Back In Stock modal', error)
      }
    },

    /**
     * Toggle the state of the 'Size Guide' popup.
     * - Should ideally emit event to Pinia rather than EventBus,
     * so if this ever becomes more complex, explore that.
     */
    toggleSizeGuidePopup() {
      mint.EventBus.dispatch('product:openSizeGuide', {
        type: this.product.types.shopify,
        handle: this.product.handle,
      })
    },
  },
}
</script>
